import './KirHaplotypeTable.css';
import { useMemo, useRef, useState } from 'react';
import {
  KIR_FAMILIES,
  KIR_GENE_GROUPS,
  KIR_HAPLOTYPES,
  KIR_LEGEND,
  KIR_POSITIONS,
  kirFamilyOf,
} from '../../database/kir-haplotypes';

const matchesQuery = (haplotype, query) =>
  `${haplotype.name} ${haplotype.local} ${haplotype.note}`
    .toLowerCase()
    .includes(query);

const KirHaplotypeTable = () => {
  const [query, setQuery] = useState('');
  const [family, setFamily] = useState('');
  const [detail, setDetail] = useState(null);
  const dialogRef = useRef(null);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return KIR_HAPLOTYPES.filter(
      (haplotype) =>
        (!family || kirFamilyOf(haplotype) === family) &&
        (!needle || matchesQuery(haplotype, needle))
    );
  }, [query, family]);

  const openDetail = (haplotype, gene, copies) => {
    setDetail({ haplotype, gene, copies });
    dialogRef.current?.showModal();
  };

  const closeDetail = () => dialogRef.current?.close();

  return (
    <div className='kir-explorer'>
      <div className='kir-controls'>
        <label className='kir-control'>
          Find haplotype
          <input
            type='search'
            className='kir-input'
            placeholder='Name or variation note'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <label className='kir-control'>
          Family
          <select
            className='kir-input'
            value={family}
            onChange={(event) => setFamily(event.target.value)}
          >
            <option value=''>All families</option>
            {KIR_FAMILIES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <p className='kir-count' role='status'>
          {rows.length} of {KIR_HAPLOTYPES.length} haplotypes
        </p>
      </div>

      <ul className='kir-legend'>
        {KIR_LEGEND.map((entry) => (
          <li key={entry.group}>
            <span className={`kir-gene kir-gene-${entry.group}`}>
              {entry.label}
            </span>
          </li>
        ))}
      </ul>

      <div className='kir-viewport' tabIndex='0'>
        <table className='kir-table'>
          <caption className='kir-table-caption'>
            Gene content across KIR haplotypes. Corresponding positions align
            vertically, centromeric to telomeric, and ×2 marks two recorded
            copies. Select a gene for its copy count and haplotype variation.
          </caption>
          <thead>
            <tr>
              <th scope='col'>Haplotype</th>
              {KIR_POSITIONS.map((position) => (
                <th key={position.join('/')} scope='col'>
                  {/* Stacked rather than "A / B" inline: the joined label is
                      wider than any chip and would set the column width. */}
                  {position.map((gene) => (
                    <span key={gene} className='kir-head-gene'>
                      {gene}
                    </span>
                  ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((haplotype, index) => {
              const startsFamily =
                index > 0 && kirFamilyOf(rows[index - 1]) !== kirFamilyOf(haplotype);
              return (
                <tr
                  key={haplotype.name}
                  className={startsFamily ? 'kir-new-family' : undefined}
                >
                  <th scope='row'>
                    {haplotype.name}
                    <span className='kir-local'>{haplotype.local}</span>
                    {haplotype.note && (
                      <span className='kir-row-note'>{haplotype.note}</span>
                    )}
                  </th>
                  {KIR_POSITIONS.map((position) => {
                    const present = position.filter(
                      (gene) => haplotype.values[gene] !== null
                    );
                    return (
                      <td key={position.join('/')} className='kir-cell'>
                        {present.length === 0 ? (
                          <span
                            className='kir-empty'
                            title='No gene copy recorded at this position'
                          >
                            –
                          </span>
                        ) : (
                          present.map((gene) => {
                            const copies = haplotype.values[gene];
                            return (
                              <button
                                key={gene}
                                type='button'
                                className={`kir-gene kir-gene-${KIR_GENE_GROUPS[gene]}`}
                                aria-label={`${gene}, ${copies} ${
                                  copies === 1 ? 'copy' : 'copies'
                                } in ${haplotype.name}. Open details`}
                                onClick={() =>
                                  openDetail(haplotype, gene, copies)
                                }
                              >
                                {gene}
                                {copies > 1 && (
                                  <span className='kir-copies'>×{copies}</span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className='kir-no-results'>
          No haplotype matches that search. Clear the filters to see all{' '}
          {KIR_HAPLOTYPES.length}.
        </p>
      )}

      <div className='kir-notes'>
        <h4 className='kir-notes-title'>Reading this table</h4>
        <p>
          A dash means no gene copy was recorded at that position. Blocks show
          gene content, not physical distances or the order of duplicated
          segments. C and T distinguish centromeric and telomeric entries, and
          2DS35 represents the 2DS3/2DS5 group. Question marks in a variation
          note mark an interpretation the source left unresolved. Colors
          distinguish diagram features, not insertion or deletion status.
        </p>
      </div>

      <dialog
        className='kir-dialog'
        ref={dialogRef}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <>
            <button
              type='button'
              className='kir-dialog-close'
              onClick={closeDetail}
            >
              Close ×
            </button>
            <h4 className='kir-dialog-title'>{detail.gene}</h4>
            <dl className='kir-dialog-fields'>
              <dt>Haplotype</dt>
              <dd>{detail.haplotype.name}</dd>
              <dt>Family</dt>
              <dd>{kirFamilyOf(detail.haplotype)}</dd>
              <dt>Gene copies</dt>
              <dd>
                {detail.copies === 1 ? '1 copy' : `${detail.copies} copies`}{' '}
                recorded
              </dd>
              {/[CT]$/.test(detail.gene) && (
                <>
                  <dt>Region</dt>
                  <dd>
                    {detail.gene.endsWith('C')
                      ? 'Centromeric entry'
                      : 'Telomeric entry'}
                  </dd>
                </>
              )}
              <dt>Haplotype variation</dt>
              <dd>
                {detail.haplotype.note.trim() === '?'
                  ? 'Variation not characterized'
                  : detail.haplotype.note.trim() ||
                    'No additional variation annotation'}
              </dd>
            </dl>
            <p className='kir-dialog-note'>
              Variation annotations describe the whole haplotype and may involve
              other genes.
              {detail.haplotype.note.includes('?') &&
                ' This annotation includes an unresolved interpretation.'}
              {detail.copies > 1 &&
                ' Copy count does not specify the physical order of duplicated segments.'}
            </p>
          </>
        )}
      </dialog>
    </div>
  );
};

export default KirHaplotypeTable;
